import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Trash2, RefreshCw, CheckCircle, XCircle, Loader } from 'lucide-react';
import Modal from '../../components/Modal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export default function CalendarIntegrationsPage() {
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [connecting, setConnecting] = useState(false);

    useEffect(() => {
        fetchIntegrations();
        
        // Check for OAuth callback results
        const urlParams = new URLSearchParams(window.location.search);
        const successParam = urlParams.get('success');
        const errorParam = urlParams.get('error');
        
        if (successParam) {
            if (successParam === 'google_connected') {
                setSuccess('Google Calendar connected successfully!');
            } else if (successParam === 'microsoft_connected') {
                setSuccess('Microsoft Calendar connected successfully!');
            }
            // Clear URL params
            window.history.replaceState({}, document.title, window.location.pathname);
            fetchIntegrations();
        }
        
        if (errorParam) {
            setError(`Connection failed: ${errorParam}`);
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []);

    const fetchIntegrations = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/calendar-integrations`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setIntegrations(data);
            } else {
                setError('Failed to fetch calendar integrations');
            }
        } catch (error) {
            console.error('Error fetching calendar integrations:', error);
            setError('Failed to fetch calendar integrations');
        } finally {
            setLoading(false);
        }
    };

    const handleSync = async (integrationId) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/calendar-integrations/${integrationId}/sync`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                alert('Sync initiated');
                fetchIntegrations();
            } else {
                setError('Failed to initiate sync');
            }
        } catch (error) {
            console.error('Error syncing calendar:', error);
            setError('Failed to initiate sync');
        }
    };

    const handleDelete = async (integrationId) => {
        if (!window.confirm('Are you sure you want to delete this calendar integration?')) {
            return;
        }

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/calendar-integrations/${integrationId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                fetchIntegrations();
            } else {
                setError('Failed to delete integration');
            }
        } catch (error) {
            console.error('Error deleting integration:', error);
            setError('Failed to delete integration');
        }
    };

    const handleConnectGoogle = async () => {
        try {
            setConnecting(true);
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/calendar-integrations/oauth/google`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                // Redirect to Google OAuth
                window.location.href = data.authUrl;
            } else {
                const errorData = await response.json();
                setError(errorData.message || 'Failed to initiate Google Calendar connection');
                setConnecting(false);
            }
        } catch (error) {
            console.error('Error connecting Google Calendar:', error);
            setError('Failed to initiate Google Calendar connection');
            setConnecting(false);
        }
    };

    const handleConnectMicrosoft = async () => {
        try {
            setConnecting(true);
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/calendar-integrations/oauth/microsoft`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                // Redirect to Microsoft OAuth
                window.location.href = data.authUrl;
            } else {
                const errorData = await response.json();
                setError(errorData.message || 'Failed to initiate Microsoft Calendar connection');
                setConnecting(false);
            }
        } catch (error) {
            console.error('Error connecting Microsoft Calendar:', error);
            setError('Failed to initiate Microsoft Calendar connection');
            setConnecting(false);
        }
    };

    const getIntegrationIcon = (type) => {
        switch (type) {
            case 'google':
                return '📅';
            case 'outlook':
            case 'microsoft':
                return '📧';
            case 'apple':
            case 'icloud':
                return '🍎';
            case 'ical':
                return '📆';
            default:
                return '📅';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-500">Loading calendar integrations...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Calendar Integrations</h1>
                    <p className="text-gray-600 mt-1">Connect your calendar to sync availability</p>
                </div>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                    <Plus size={20} />
                    Add Integration
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center justify-between">
                    <span>{error}</span>
                    <button onClick={() => setError('')} className="text-red-700 hover:text-red-900">
                        ×
                    </button>
                </div>
            )}

            {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded flex items-center justify-between">
                    <span>{success}</span>
                    <button onClick={() => setSuccess('')} className="text-green-700 hover:text-green-900">
                        ×
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {integrations.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-gray-500">
                        No calendar integrations found. Add one to get started.
                    </div>
                ) : (
                    integrations.map((integration) => (
                        <div key={integration.id} className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <span className="text-2xl">{getIntegrationIcon(integration.integration_type)}</span>
                                    <div>
                                        <h3 className="font-semibold text-gray-900 capitalize">
                                            {integration.integration_type}
                                        </h3>
                                        {integration.calendar_name && (
                                            <p className="text-sm text-gray-500">{integration.calendar_name}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {integration.is_active ? (
                                        <CheckCircle className="text-green-500" size={20} />
                                    ) : (
                                        <XCircle className="text-gray-400" size={20} />
                                    )}
                                </div>
                            </div>

                            <div className="space-y-2 mb-4">
                                <div className="text-sm">
                                    <span className="text-gray-500">Sync Direction:</span>{' '}
                                    <span className="font-medium">{integration.sync_direction}</span>
                                </div>
                                {integration.last_sync_at && (
                                    <div className="text-sm">
                                        <span className="text-gray-500">Last Sync:</span>{' '}
                                        <span className="font-medium">
                                            {new Date(integration.last_sync_at).toLocaleString()}
                                        </span>
                                    </div>
                                )}
                                {integration.sync_count > 0 && (
                                    <div className="text-sm">
                                        <span className="text-gray-500">Total Syncs:</span>{' '}
                                        <span className="font-medium">{integration.sync_count}</span>
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleSync(integration.id)}
                                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                                    disabled={!integration.is_active}
                                >
                                    <RefreshCw size={16} />
                                    Sync
                                </button>
                                <button
                                    onClick={() => handleDelete(integration.id)}
                                    className="px-3 py-2 bg-red-50 text-red-700 rounded hover:bg-red-100"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Add Integration Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setConnecting(false);
                }}
                title="Connect Calendar"
            >
                <div className="space-y-4">
                    <p className="text-gray-600">
                        Connect your calendar to sync appointments automatically. Choose your calendar provider:
                    </p>
                    
                    <div className="space-y-3">
                        {/* Google Calendar button hidden until integration is configured */}

                        <button
                            onClick={handleConnectMicrosoft}
                            disabled={connecting}
                            className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {connecting ? (
                                <Loader className="animate-spin" size={20} />
                            ) : (
                                <>
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#0078D4">
                                        <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zM24 11.4H12.6V0H24v11.4z"/>
                                    </svg>
                                    <span className="font-medium">Connect Microsoft Calendar</span>
                                </>
                            )}
                        </button>

                        <div className="text-xs text-gray-500 pt-2">
                            <p>Note: Google Calendar and Apple Calendar (iCloud) support coming soon.</p>
                        </div>
                    </div>

                    <div className="flex justify-end pt-4">
                        <button
                            onClick={() => {
                                setIsModalOpen(false);
                                setConnecting(false);
                            }}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                            disabled={connecting}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

